<script setup lang="ts">
import { Btn, Icon, Popup } from '@/shared/ui';

import { formatMessageAttachmentSize } from '../../../lib';
import type { MessageInfoSchemaType } from '../../../model';

const { opened, attachment, isImageAttachment } = defineProps<{
  opened: boolean;
  attachment: NonNullable<MessageInfoSchemaType['attachments']>[number] | null;
  isImageAttachment: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'download'): void;
  (e: 'update:opened', value: boolean): void;
}>();
</script>

<template>
  <Popup
    :show="opened"
    overlay
    close-on-click-overlay
    class="message-attachment-popup"
    @update:show="(value) => emit('update:opened', value)"
  >
    <div
      class="flex max-h-[calc(100dvh-2rem)] w-[min(88vw,760px)] flex-col gap-3 overflow-hidden p-3"
    >
      <div
        class="flex min-w-0 items-center gap-3"
        :class="isImageAttachment ? 'justify-between' : 'justify-end'"
      >
        <div
          v-if="isImageAttachment"
          class="min-w-0 truncate text-sm font-semibold text-[var(--theme-text-primary)]"
        >
          {{ attachment?.originalName ?? '' }}
        </div>

        <div class="flex shrink-0 items-center gap-2">
          <Btn
            size="small"
            round
            icon-container
            :disabled="!attachment"
            @click="emit('download')"
          >
            <Icon
              name="lucide:download"
              width="18"
              height="18"
            />
          </Btn>

          <Btn
            size="small"
            round
            icon-container
            @click="emit('close')"
          >
            <Icon
              name="material-symbols:close"
              width="18"
              height="18"
            />
          </Btn>
        </div>
      </div>

      <div
        class="flex min-h-0 min-w-0 items-center justify-center overflow-hidden rounded-[24px] border border-[var(--theme-border-subtle)] bg-[var(--theme-surface-raised)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
      >
        <img
          v-if="attachment && isImageAttachment"
          :src="attachment.url"
          :alt="attachment.originalName"
          class="block max-h-[calc(100dvh-10rem)] max-w-full object-contain"
        />

        <div
          v-else-if="attachment"
          class="flex w-full max-w-[360px] items-center gap-4"
        >
          <div
            class="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-[20px] bg-[color-mix(in_srgb,var(--button-primary-color),transparent_85%)] text-[var(--button-primary-color)]"
          >
            <Icon
              name="lucide:file"
              width="44"
              height="44"
            />
          </div>

          <div class="flex min-w-0 flex-col gap-1.5">
            <div class="text-base font-semibold break-words text-[var(--theme-text-primary)]">
              {{ attachment.originalName }}
            </div>

            <div class="text-[13px] leading-[1.3] break-words text-[var(--theme-text-secondary)]">
              {{ formatMessageAttachmentSize(attachment.size) }}
            </div>

            <div class="text-[13px] leading-[1.3] break-words text-[var(--theme-text-secondary)]">
              {{ attachment.extension }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Popup>
</template>
